import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Badge,
  Popover,
  Dialog,
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "https://apiweb.hitpoly.com/ajax/notifications.php";

// Mapa completo de emojis por entidad (todos los proyectos)
const ENTITY_EMOJIS = {
  // HOLDING
  friend_request_sent: "👤",
  friend_request_accepted: "✅",
  post_created_by_friend: "📝",
  post_liked: "❤️",
  post_commented: "💬",
  comment_replied: "↩️",
  user_mentioned: "🔔",
  post_shared: "🔄",
  profile_viewed: "👀",
  community_joined: "👥",
  new_message: "✉️",
  message_read: "👁️",

  // CRM
  prospect_created: "👤",
  prospect_assigned: "👤",
  deal_stage_changed: "📊",
  deal_won: "🏆",
  deal_lost: "❌",
  task_assigned: "📋",
  task_due_soon: "⏰",
  task_overdue: "🔴",
  task_completed: "✅",
  meeting_scheduled: "📅",
  meeting_reminder: "⏰",
  meeting_cancelled: "🚫",
  email_opened: "📨",
  email_clicked: "🔗",
  whatsapp_received: "💬",
  automation_failed: "⚠️",
  automation_completed: "✅",
  form_submitted: "📝",
  contract_signed: "✍️",
  contract_expiring: "📄",
  payment_received: "💰",
  payment_overdue: "🔴",
  follow_up_reminder: "📌",
  note_mention: "📝",
  company_invited: "🏢",
  integration_disconnected: "🔌",
  subscription_expiring: "📅",

  // ACADEMY
  course_published: "📚",
  new_enrollment: "👨‍🎓",
  course_completed: "🎓",
  course_expiring: "⏳",
  new_class_added: "📹",
  new_module_added: "📚",
  class_updated: "🔄",
  course_offer_created: "🏷️",
  flash_sale: "🔥",
  exam_graded: "📝",
  exam_reminder: "⏰",
  certificate_issued: "🎓",
  milestone_reached: "📈",
  instructor_request: "👨‍🏫",
  instructor_approved: "🎉",
  course_question: "❓",
  question_answered: "💡",

  // BOLSA
  new_ad_by_specialty: "💼",
  new_ad_by_sector: "💼",
  ad_activated: "✅",
  ad_expiring: "⏳",
  new_application: "📄",
  application_viewed: "👁️",
  application_accepted: "✅",
  application_rejected: "❌",
  application_status_changed: "🔄",
  new_professional_by_specialty: "👨‍💼",
  new_professional_by_sector: "👨‍💼",
  profile_created: "🎉",
  profile_completed: "⭐",
  new_service_created: "🛠️",
  service_inquiry: "💬",
  service_hired: "🤝",
  new_review: "⭐",
  company_followed: "👤",
  company_page_created: "🏢",
  portfolio_item_added: "📁",
  new_vinculation: "🔗",

  // TRELLOHIT
  task_assigned_trello: "📌",
  task_due_soon_trello: "⏰",
  task_overdue_trello: "🔴",
  task_completed_trello: "✅",
  task_moved: "➡️",
  task_updated: "✏️",
  comment_added_trello: "💬",
  comment_mentioned_trello: "🔔",
  board_invited: "📋",
  board_shared: "📋",
  project_invited: "📁",
  checklist_completed: "✅",
  due_date_approaching: "⏰",
  activity_summary: "📊",
  template_used: "📐",
  template_commented: "💬",

  // SCRAPPING
  scraping_completed: "✅",
  scraping_failed: "❌",
  scraping_progress: "⏳",
  new_leads_found: "👤",
  lead_count_threshold: "📊",
  export_ready: "📥",
  export_failed: "❌",
  api_quota_exceeded: "⚠️",
  plan_limit_reached: "📈",
  new_company_match: "🏢",

  // SYSTEM
  welcome_new_user: "🎉",
  email_verified: "✅",
  account_reactivated: "✅",
  profile_merged: "🔄",
  new_feature_available: "✨",
  maintenance_announcement: "🔧",
  terms_updated: "📋",
};

const PROJECT_LABELS = {
  holding: "Social",
  crm: "CRM",
  academy: "Academia",
  bolsa: "Bolsa Prof.",
  trellohit: "Trellohit",
  scrapping: "Scraping",
  system: "Sistema",
};

const PROJECT_COLORS = {
  holding: "#8b5cf6",
  crm: "#2563eb",
  academy: "#ea580c",
  bolsa: "#059669",
  trellohit: "#0284c7",
  scrapping: "#dc2626",
  system: "#6b7280",
};

const getTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "ahora";
  if (diffMins < 60) return `hace ${diffMins}m`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  if (diffDays < 30) return `hace ${diffDays}d`;
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

const NotificationBell = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [loading, setLoading] = useState(false);

  const userId = user?.user_id || user?.id;

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}?action=list-grouped&user_id=${userId}&limit=15`
      );
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setNotifications(json.data);
      }
    } catch (err) {
      console.error("Error cargando notificaciones:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(
        `${API_BASE}?action=unread-count&user_id=${userId}`
      );
      const json = await res.json();
      if (json.success) {
        setUnreadCount(json.count);
      }
    } catch (err) {
      console.error("Error cargando contador:", err);
    }
  }, [userId]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const handleClick = (event) => {
    console.log("Y:", 64);
    setAnchorPosition({ top: 64, left: window.innerWidth - 1 });
    setOpen(true);
    loadNotifications();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await fetch(`${API_BASE}?action=mark-read&id=${id}&user_id=${userId}`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marcando como leída:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await fetch(`${API_BASE}?action=read-all&user_id=${userId}`);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: 1 }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marcando todas como leídas:", err);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      handleMarkAsRead(notif.id);
    }
    if (notif.action_url) {
      window.open(notif.action_url, "_blank", "noopener,noreferrer");
    }
  };

  const renderContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography variant="h6" fontSize="16px" fontWeight="700">
          Notificaciones
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {isMobile && (
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          )}
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              startIcon={<DoneAllIcon fontSize="small" />}
              sx={{
                color: "#8b5cf6",
                fontWeight: 600,
                textTransform: "none",
                fontSize: 12,
                "&:hover": { bgcolor: "#f5f3ff" },
              }}
            >
              {unreadCount} sin leer
            </Button>
          )}
        </Box>
      </Box>

      <List sx={{ p: 0, flex: 1, overflowY: "auto" }}>
        {loading && notifications.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#8b5cf6" }} />
          </Box>
        )}

        {!loading && notifications.length === 0 && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <NotificationsNoneIcon
              sx={{ fontSize: 48, color: "#d1d5db", mb: 1.5 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              No hay notificaciones
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Las notificaciones apareceran aqui
            </Typography>
          </Box>
        )}

        {notifications.map((notif, index) => {
          const emoji = notif.icon_emoji || ENTITY_EMOJIS[notif.source_entity] || "🔔";
          const projectLabel = PROJECT_LABELS[notif.source_project] || notif.source_project;
          const projectColor = PROJECT_COLORS[notif.source_project] || "#6b7280";
          const hasGroup = notif.group_count > 1;
          const isHighPriority = notif.priority >= 1;

          return (
            <React.Fragment key={notif.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                alignItems="flex-start"
                sx={{
                  cursor: "pointer",
                  bgcolor: !notif.is_read
                    ? "linear-gradient(to right, #f5f3ff, #ffffff)"
                    : "transparent",
                  py: 1.5,
                  px: 2,
                  borderLeft: !notif.is_read ? "3px solid #8b5cf6" : "3px solid transparent",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    bgcolor: !notif.is_read ? "#ede9fe" : "#f9fafb",
                  },
                }}
                onClick={() => handleNotificationClick(notif)}
              >
                <ListItemAvatar sx={{ minWidth: 52 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                      isHighPriority ? (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: "#dc2626",
                            border: "2px solid white",
                          }}
                        />
                      ) : null
                    }
                  >
                    <Avatar
                      src={notif.actor_avatar || undefined}
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor: !notif.actor_avatar ? projectColor : undefined,
                        fontSize: 20,
                        boxShadow: !notif.actor_avatar
                          ? "0 2px 8px rgba(0,0,0,0.1)"
                          : undefined,
                      }}
                    >
                      {!notif.actor_avatar ? emoji : undefined}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ mb: 0.3 }}>
                      <Typography
                        variant="body2"
                        fontWeight={!notif.is_read ? "700" : "500"}
                        sx={{
                          lineHeight: 1.3,
                          display: "inline",
                          mr: 0.5,
                        }}
                      >
                        {notif.title}
                      </Typography>
                      {hasGroup && (
                        <Chip
                          label={`+${notif.group_count - 1}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: "#f5f3ff",
                            color: "#8b5cf6",
                            ml: 0.5,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      {notif.body_text && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            lineHeight: 1.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 290,
                            fontSize: 12,
                          }}
                        >
                          {notif.body_text}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.3,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#9ca3af", fontSize: 11 }}
                        >
                          {getTimeAgo(notif.created_at)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#9ca3af", fontSize: 11 }}
                        >
                          ·
                        </Typography>
                        <Chip
                          label={projectLabel}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: 9,
                            fontWeight: 600,
                            bgcolor: `${projectColor}15`,
                            color: projectColor,
                            borderRadius: "4px",
                            "& .MuiChip-label": { px: 0.5 },
                          }}
                        />
                      </Box>
                    </Box>
                  }
                />

                {!notif.is_read && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#8b5cf6",
                      flexShrink: 0,
                      mt: 1.5,
                      ml: 0.5,
                    }}
                  />
                )}
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      {notifications.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            size="small"
            onClick={handleClose}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              "&:hover": { bgcolor: "#f1f5f9", borderRadius: "8px" },
            }}
          >
            Ver todas las notificaciones
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="medium"
        sx={{
          color: "#64748b",
          bgcolor: "#f8fafc",
          display: "flex",
          "&:hover": { bgcolor: "#f1f5f9" },
          position: "relative",
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 11,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              fontWeight: 700,
            },
          }}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      {isMobile ? (
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen
          sx={{ zIndex: 9999 }}
          PaperProps={{
            sx: { borderRadius: 0 },
          }}
        >
          {renderContent()}
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorReference="anchorPosition"
          anchorPosition={anchorPosition}
          onClose={handleClose}
          sx={{ zIndex: 9999 }}
          PaperProps={{
            sx: {
              width: 400,
              maxHeight: 480,
              borderRadius: "12px",
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #e5e7eb",
              mt: 1,
              mr: 1.5,
              overflow: "hidden",
            },
          }}
        >
          {renderContent()}
        </Popover>
      )}
    </>
  );
};

export default NotificationBell;
